import { useState, useEffect, useContext, createContext, ReactNode } from 'react';

interface HowlerContextType {
  playSound: (name: string) => void;
  enabled: boolean;
  toggleEnabled: () => void;
}

const HowlerContext = createContext<HowlerContextType | undefined>(undefined);

type OscillatorType = 'sine' | 'square' | 'sawtooth' | 'triangle';

let audioContext: AudioContext | null = null;

function getAudioContext() {
  if (!audioContext) {
    audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
  }
  return audioContext;
}

function playTone(frequency: number, duration: number, type: OscillatorType = 'sine', volume = 0.1) {
  const ctx = getAudioContext();
  if (ctx.state === 'suspended') ctx.resume();

  const oscillator = ctx.createOscillator();
  const gainNode = ctx.createGain();

  oscillator.type = type;
  oscillator.frequency.setValueAtTime(frequency, ctx.currentTime);
  oscillator.frequency.exponentialRampToValueAtTime(frequency * 0.5, ctx.currentTime + duration);

  gainNode.gain.setValueAtTime(volume, ctx.currentTime);
  gainNode.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + duration);

  oscillator.connect(gainNode);
  gainNode.connect(ctx.destination);

  oscillator.start(ctx.currentTime);
  oscillator.stop(ctx.currentTime + duration);
}

const soundPresets = {
  click: () => playTone(800, 0.08, 'sine', 0.15),
  hover: () => playTone(1200, 0.05, 'sine', 0.08),
  whoosh: () => {
    playTone(200, 0.3, 'sawtooth', 0.1);
    setTimeout(() => playTone(400, 0.2, 'sawtooth', 0.08), 50);
  },
};

export function HowlerProvider({ children }: { children: ReactNode }) {
  const [enabled, setEnabled] = useState(true);

  useEffect(() => {
    const saved = localStorage.getItem('soundEnabled');
    if (saved !== null) setEnabled(saved === 'true');
  }, []);

  const toggleEnabled = () => {
    const newEnabled = !enabled;
    setEnabled(newEnabled);
    localStorage.setItem('soundEnabled', String(newEnabled));
  };

  const playSound = (name: string) => {
    if (enabled && soundPresets[name as keyof typeof soundPresets]) {
      soundPresets[name as keyof typeof soundPresets]();
    }
  };

  return (
    <HowlerContext.Provider value={{ playSound, enabled, toggleEnabled }}>
      {children}
    </HowlerContext.Provider>
  );
}

export function useHowler() {
  const context = useContext(HowlerContext);
  if (!context) {
    throw new Error('useHowler must be used within a HowlerProvider');
  }
  return context;
}